// Property-panel primitives. Controls register a sync() with a Binder so the panel can refresh
// values after any change (undo, drag, typing elsewhere) without rebuilding — and never while focused.
// Every value-setting control shows a rejected edit (e.g. a LabelError) inline under itself; the model
// keeps its last valid value because label.js writes nothing on error.
import { h, mm, unitsFromMm } from '../dom.js';
import { safeSet } from '../state.js';

let seq = 0;
const uid = (p) => `${p}-${++seq}`;
const isFocused = (el) => el === document.activeElement || el.contains(document.activeElement);

export class Binder {
  constructor() {
    this.fns = [];
  }
  add(fn) {
    this.fns.push(fn);
    fn();
    return fn;
  }
  sync() {
    for (const fn of this.fns) fn();
  }
}

// ----- inline error / note slots -------------------------------------------------------------------
function slot(cls, role) {
  const el = h('div', { class: `hint ${cls}`, role, 'aria-live': 'polite', hidden: true });
  let timer = 0;
  return {
    el,
    show(msg, ms = 0) {
      clearTimeout(timer);
      el.textContent = msg || '';
      el.hidden = !msg;
      if (msg && ms) timer = setTimeout(() => (el.hidden = true), ms);
    },
  };
}

/** Wrap a control with an error line; returns { wrap, report(result) }. report() shows Error results. */
function withError(control, { note = false } = {}) {
  const err = slot('field-error', 'alert');
  const nt = note ? slot('field-note', 'status') : null;
  const wrap = h('div', { class: 'ctl', style: { display: 'grid', gap: '3px', minWidth: '0' } }, control, err.el, nt && nt.el);
  wrap.control = control;
  const report = (result) => {
    if (result instanceof Error) err.show(result.message || String(result));
    else err.show('');
    return result;
  };
  return { wrap, report, note: nt, error: err };
}

// ----- sections ----------------------------------------------------------------------------------
let collapsedState = {};
try {
  collapsedState = JSON.parse(localStorage.getItem('labels.sections') || '{}');
} catch {
  collapsedState = {};
}

export function section(title, key, children, { collapsed = false, extra = null } = {}) {
  const isCollapsed = key in collapsedState ? collapsedState[key] : collapsed;
  const bodyId = uid('sec');
  const sec = h('div', { class: `props-section${isCollapsed ? ' collapsed' : ''}` });
  const chev = h('span', { 'aria-hidden': 'true', html: '<svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 9l6 6 6-6"/></svg>' });
  const head = h(
    'div',
    { class: 'props-section-head', role: 'button', tabindex: '0', 'aria-expanded': String(!isCollapsed), 'aria-controls': bodyId },
    h('span', { style: { display: 'flex', alignItems: 'center', gap: '8px', minWidth: '0' } }, h('h3', null, title), extra),
    chev,
  );
  const toggle = () => {
    const now = !sec.classList.contains('collapsed');
    sec.classList.toggle('collapsed', now);
    head.setAttribute('aria-expanded', String(!now));
    collapsedState[key] = now;
    safeSet('labels.sections', JSON.stringify(collapsedState));
  };
  head.addEventListener('click', toggle);
  head.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  });
  sec.append(head, h('div', { class: 'props-section-body', id: bodyId }, children));
  return sec;
}

export function row(labelText, control, { scrub = null } = {}) {
  const target = control.matches && control.matches('input,select,textarea') ? control : control.querySelector && control.querySelector('input,select,textarea');
  if (target && !target.id) target.id = uid('ctl');
  const lab = h(
    'label',
    {
      for: target ? target.id : null,
      class: scrub ? 'scrub-label' : null,
      title: scrub ? 'Drag to change (Shift ×10, Alt ×0.1)' : null,
      style: scrub ? { alignSelf: 'start', paddingTop: '6px' } : { color: 'var(--ink-3)', fontSize: '11px', alignSelf: 'start', paddingTop: '6px' },
    },
    labelText,
  );
  if (scrub && target) attachScrub(lab, target, scrub);
  return h('div', { class: 'field-grid' }, lab, control);
}

export const full = (...children) => h('div', { class: 'field-grid full' }, ...children);

export function hint(text, style) {
  return h('div', { class: 'hint', style }, text);
}

/** Drag a label horizontally to scrub a number input. step = value change per 2 px. */
export function attachScrub(label, input, { step = 0.1, decimals = 2 } = {}) {
  label.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || input.disabled) return;
    e.preventDefault();
    const startX = e.clientX;
    const start = parseFloat(input.value) || 0;
    label.setPointerCapture(e.pointerId);
    document.body.style.cursor = 'ew-resize';
    const move = (ev) => {
      const mult = ev.shiftKey ? 10 : ev.altKey ? 0.1 : 1;
      const v = start + Math.round((ev.clientX - startX) / 2) * step * mult;
      input.value = v.toFixed(decimals);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };
    const up = () => {
      label.removeEventListener('pointermove', move);
      label.removeEventListener('pointerup', up);
      label.removeEventListener('pointercancel', up);
      document.body.style.cursor = '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    label.addEventListener('pointermove', move);
    label.addEventListener('pointerup', up);
    label.addEventListener('pointercancel', up);
  });
}

// ----- inputs ----------------------------------------------------------------------------------------
/** Number input shown in mm, stored in whole file units (0.01 mm). set() may return an Error to display. */
export function mmInput(b, { ariaLabel, get, set, min = -Infinity, max = Infinity, step = 0.1, snap = null, decimals = 2, unit = 'mm' }) {
  const input = h('input', { class: 'input', type: 'number', step: String(step), inputmode: 'decimal', 'aria-label': ariaLabel });
  const fmt = () => {
    const v = get();
    return v === null || v === undefined || Number.isNaN(v) ? '' : mm(v, decimals);
  };
  const { wrap, report } = withError(h('div', { class: 'with-unit' }, input, h('span', { class: 'unit', 'aria-hidden': 'true' }, unit)));
  input.addEventListener('input', () => {
    let u = unitsFromMm(input.value); // already rounded to whole units
    if (u === null) return;
    u = Math.round(Math.max(min, Math.min(max, u)));
    if (snap) u = Math.round(snap(u));
    if (u !== get()) report(set(u));
    else report(null);
  });
  input.addEventListener('change', () => {
    input.value = fmt(); // show the value the model kept
  });
  b.add(() => {
    if (!isFocused(input)) input.value = fmt();
  });
  wrap.control = input;
  wrap.report = report;
  return wrap;
}

/** Integer input (offsets, IDs). */
export function intInput(b, { ariaLabel, get, set, min = -99999, max = 99999, unit = null, placeholder = null }) {
  const input = h('input', { class: 'input', type: 'number', step: '1', inputmode: 'numeric', 'aria-label': ariaLabel, placeholder });
  const inner = unit ? h('div', { class: 'with-unit' }, input, h('span', { class: 'unit', 'aria-hidden': 'true' }, unit)) : input;
  const { wrap, report } = withError(inner);
  input.addEventListener('input', () => {
    if (input.value.trim() === '' || input.value === '-') return;
    const n = Number(input.value);
    if (!Number.isFinite(n)) return;
    if (!Number.isInteger(n)) {
      report(new Error('Whole numbers only'));
      return;
    }
    const v = Math.max(min, Math.min(max, n));
    if (v !== get()) report(set(v));
    else report(null);
  });
  input.addEventListener('change', () => {
    input.value = String(get() ?? '');
  });
  b.add(() => {
    if (!isFocused(input)) input.value = String(get() ?? '');
  });
  wrap.control = input;
  return wrap;
}

const ONE_LINE_HINT = 'Labels print one line per text field — add another text field for a second line';
const PASTE_NOTE = 'Line breaks in the pasted text were replaced by spaces.';

/** Text input / textarea. Line breaks are never written (the printer prints one line per field). */
export function textInput(b, { ariaLabel, get, set, multiline = false, placeholder = '', primary = false }) {
  const el = h(multiline ? 'textarea' : 'input', {
    class: multiline ? 'input textarea' : 'input',
    type: multiline ? null : 'text',
    'aria-label': ariaLabel,
    placeholder,
    spellcheck: 'false',
    autocomplete: 'off',
    'data-primary': primary ? 'true' : null,
    dir: 'auto',
  });
  const { wrap, report, note } = withError(el, { note: true });
  const commit = () => {
    if (/[\r\n]/.test(el.value)) {
      const pos = el.selectionStart;
      el.value = el.value.replace(/\r\n|\r|\n/g, ' ');
      if (pos !== null) el.setSelectionRange(pos, pos);
    }
    report(set(el.value));
  };
  el.addEventListener('input', commit);
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.isComposing && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (multiline) note.show(ONE_LINE_HINT, 6000);
      else el.blur();
    }
  });
  el.addEventListener('paste', (e) => {
    const text = e.clipboardData && e.clipboardData.getData('text');
    if (!text || !/[\r\n]/.test(text)) return;
    e.preventDefault();
    const clean = text.replace(/\r\n|\r|\n/g, ' ');
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.setRangeText(clean, start, end, 'end');
    note.show(PASTE_NOTE, 6000);
    commit();
  });
  b.add(() => {
    if (!isFocused(el)) el.value = get() ?? '';
  });
  return wrap;
}

/** options: [{ value, label }] or () => same (re-rendered when the list changes). */
export function selectInput(b, { ariaLabel, options, get, set }) {
  const el = h('select', { class: 'select', 'aria-label': ariaLabel });
  const { wrap, report } = withError(el);
  let sig = '';
  el.addEventListener('change', () => {
    report(set(el.value));
    el.value = get() ?? '';
  });
  b.add(() => {
    const list = typeof options === 'function' ? options() : options;
    const nextSig = JSON.stringify(list);
    if (nextSig !== sig) {
      sig = nextSig;
      el.replaceChildren(...list.map((o) => h('option', { value: o.value }, o.label)));
    }
    if (!isFocused(el)) el.value = get() ?? '';
  });
  return wrap;
}

/** Independent on/off toggles: [{ label, title, get, set, style, visible }] */
export function toggles(b, items) {
  const rowEl = h('div', { class: 'toggle-row' });
  const { wrap, report } = withError(rowEl);
  for (const it of items) {
    const btn = h('button', { class: 'tog', type: 'button', title: it.title, 'aria-label': it.title || it.label, style: it.style }, it.label);
    btn.addEventListener('click', () => report(it.set(!it.get())));
    b.add(() => {
      const on = !!it.get();
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-pressed', String(on));
      if (it.visible) btn.hidden = !it.visible();
    });
    rowEl.appendChild(btn);
  }
  return wrap;
}

/** Single choice: options [{ value, label, title }] */
export function segmented(b, { ariaLabel, options, get, set }) {
  const group = h('div', { class: 'toggle-row', role: 'radiogroup', 'aria-label': ariaLabel });
  const { wrap, report } = withError(group);
  const btns = options.map((o) => {
    const btn = h('button', { class: 'tog', type: 'button', role: 'radio', title: o.title || o.label }, o.label);
    btn.addEventListener('click', () => report(set(o.value)));
    group.appendChild(btn);
    return [o, btn];
  });
  group.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = options.findIndex((o) => o.value === get());
    const n = options[(i + (e.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length];
    report(set(n.value));
    const btn = btns.find(([o]) => o.value === n.value);
    if (btn) btn[1].focus();
  });
  b.add(() => {
    const cur = get();
    for (const [o, btn] of btns) {
      const on = o.value === cur;
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-checked', String(on));
      btn.tabIndex = on ? 0 : -1;
    }
  });
  return wrap;
}

/** Live message line. fn() -> { kind: 'bad'|'warn'|'good'|'info'|null, text } */
export function message(b, fn, { role = 'status' } = {}) {
  const el = h('div', { class: 'hint', role, 'aria-live': 'polite' });
  const colors = { bad: 'var(--bad)', warn: 'var(--warn)', good: 'var(--good)', info: 'var(--ink-2)' };
  b.add(() => {
    const m = fn() || {};
    el.hidden = !m.text;
    el.textContent = m.text || '';
    el.style.color = colors[m.kind] || 'var(--ink-3)';
  });
  return el;
}

/** An error line that any button handler can report into. */
export function errorLine() {
  const s = slot('field-error', 'alert');
  return { el: s.el, report: (res) => (s.show(res instanceof Error ? res.message || String(res) : ''), res) };
}

export function iconButton(label, svgHtml, onClick, { cls = 'tog' } = {}) {
  return h('button', { class: cls, type: 'button', title: label, 'aria-label': label, style: { padding: '6px 0' }, onClick, html: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">${svgHtml}</svg>` });
}
